import { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardCheck, ArrowRight, Search, Copy, Check, RotateCcw } from "lucide-react";
import {
  saveBaselineDraft,
  confirmBaseline,
  getLifecycle,
  generateCyclePhrases,
  getCyclePhraseJob,
  getBaselineDraft,
} from "@/api";
import {
  BASELINE_QUESTIONS,
  missingBaselineFields,
  isBaselineComplete,
  buildBaselinePayload,
  runSaveDraft,
  runConfirm,
  interpretPhraseJob,
  hydrateFromDraft,
} from "@/lib/baselineFlow";

const card = {
  maxWidth: 640, margin: "8vh auto", padding: "2.5rem", background: "#0f1115",
  color: "#f4f4f5", borderRadius: 20, border: "1px solid #23262d",
  boxShadow: "0 24px 60px rgba(0,0,0,.35)",
};
const textareaStyle = {
  width: "100%", marginTop: 6, padding: "0.7rem 0.85rem", borderRadius: 10, minHeight: 90,
  background: "#16181d", color: "#f4f4f5", border: "1px solid #2a2e37", fontSize: "0.95rem",
  fontFamily: "inherit", resize: "vertical",
};

// Customer-facing Day 0 Baseline for a cycle awaiting its baseline. Sequence:
// activated focus -> cycle-bound generated phrases -> test in ChatGPT/Perplexity -> Day 0 findings
// + q1-q4 -> Save Draft -> Confirm (freezes phrases). Phrases are generated strictly from the cycle's
// immutable activated_focus (never from profile / caller input) via the cycle-bound endpoints.
export default function BaselineScreen({ onConfirmed, cycleId }) {
  const [note, setNote] = useState("");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [savedKey, setSavedKey] = useState(null);
  const readyRef = useRef(false);

  // Cycle-bound phrase state.
  const [phrases, setPhrases] = useState([]);
  const [phraseSource, setPhraseSource] = useState({ title: "", audience: "" });
  const [generating, setGenerating] = useState(false);
  const [queueMsg, setQueueMsg] = useState("");
  const [phraseError, setPhraseError] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const pollRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // CB-3E recovery: on mount, hydrate from the canonical Baseline draft so phrases (and any saved
  // note/answers) survive a lost/timed-out poll or a page refresh — without regenerating. Autosave
  // is held off (readyRef) until hydration completes so it can never clobber saved values with blanks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const draft = await getBaselineDraft(cycleId);
        if (cancelled) return;
        const h = hydrateFromDraft(draft);
        setPhrases(h.phrases);
        setPhraseSource({ title: h.sourceTitle, audience: h.sourceAudience });
        setNote(h.note);
        setAnswers(h.answers);
        const key = JSON.stringify(buildBaselinePayload({
          note: h.note, answers: h.answers, phrases: h.phrases,
          sourceTitle: h.sourceTitle, sourceAudience: h.sourceAudience,
        }));
        setSavedKey(key);
      } catch (e) {
        // Fresh cycle / draft not readable yet — start empty; autosave still enabled below.
      } finally {
        if (!cancelled) readyRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const payload = buildBaselinePayload({
    note,
    answers,
    phrases,
    sourceTitle: phraseSource.title,
    sourceAudience: phraseSource.audience,
  });
  const currentKey = JSON.stringify(payload);
  const complete = isBaselineComplete({ note, answers });
  const missing = missingBaselineFields({ note, answers });

  const persist = useCallback(async () => {
    const res = await runSaveDraft({ payload, saveBaselineDraft: (p) => saveBaselineDraft(p, cycleId) });
    if (res.ok) {
      setSavedKey(currentKey);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } else {
      setError(res.message);
    }
    return res.ok;
  }, [currentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced autosave of the draft — PUT only, never advances the cycle. Held until hydration
  // completes, and skipped when the current draft already matches what was last saved/hydrated.
  useEffect(() => {
    if (!readyRef.current) return;
    if (currentKey === savedKey) return;
    const t = setTimeout(() => { persist(); }, 700);
    return () => clearTimeout(t);
  }, [currentKey, savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishJob = useCallback((res) => {
    const s = interpretPhraseJob(res);
    if (!s.done) { setQueueMsg(s.queueMsg); return false; }
    if (s.error) { setPhraseError(s.error); }
    else {
      setPhrases(s.phrases);
      setPhraseSource({ title: s.sourceTitle, audience: s.sourceAudience });
    }
    setGenerating(false);
    setQueueMsg("");
    submittingRef.current = false;
    return true;
  }, []);

  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 70;
    const tick = async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        setPhraseError("The studio is busy right now. Your work is saved — try again in a few minutes.");
        setGenerating(false); setQueueMsg(""); submittingRef.current = false; return;
      }
      try {
        const res = await getCyclePhraseJob(jobId);
        if (!finishJob(res)) pollRef.current = setTimeout(tick, 1300);
      } catch (e) {
        setPhraseError("The studio is busy right now. Your work is saved — try again in a few minutes.");
        setGenerating(false); setQueueMsg(""); submittingRef.current = false;
      }
    };
    pollRef.current = setTimeout(tick, 900);
  }, [finishJob]);

  const doGenerate = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setGenerating(true);
    setPhraseError("");
    setQueueMsg("Finding your phrases…");
    try {
      const res = await generateCyclePhrases(cycleId);
      if (!finishJob(res) && res.job_id) pollJob(res.job_id);
    } catch (e) {
      setPhraseError(e?.response?.data?.detail?.message || "Could not generate phrases. Please try again.");
      setGenerating(false); setQueueMsg(""); submittingRef.current = false;
    }
  }, [finishJob, pollJob]);

  const copyPhrase = (text, idx) => {
    navigator.clipboard?.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(-1), 1600);
  };

  const setAnswer = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const onSaveDraft = useCallback(async () => {
    setBusy(true); setError("");
    await persist();
    setBusy(false);
  }, [persist]);

  const canConfirm = complete && savedKey === currentKey && !busy;

  const onConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setBusy(true); setError("");
    const res = await runConfirm({ confirmBaseline: () => confirmBaseline(cycleId), getLifecycle });
    setBusy(false);
    if (res.ok) onConfirmed(res.lifecycle);
    else setError(res.message);
  }, [canConfirm, onConfirmed]);

  const hasPhrases = phrases.length > 0;

  return (
    <div data-testid="baseline-screen" style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#2dd4bf",
                    fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", fontSize: 12 }}>
        <ClipboardCheck size={16} /> Findability Cycle · Baseline
      </div>
      <h1 data-testid="baseline-title" style={{ fontSize: "2rem", lineHeight: 1.15, margin: ".6rem 0 .4rem" }}>
        Set your baseline
      </h1>
      <p data-testid="baseline-intro" style={{ color: "#a1a1aa", margin: 0, fontSize: ".95rem" }}>
        This is the starting measurement for your Findability Cycle — a snapshot of how visible you are
        in AI search today. Answer honestly; there are no wrong answers. Your 30 focused efforts begin
        only after you confirm this baseline.
      </p>

      {/* Step 1 — cycle-bound search phrases */}
      <div data-testid="baseline-phrase-step"
           style={{ marginTop: "1.6rem", padding: "1.1rem", borderRadius: 14,
                    background: "#111820", border: "1px solid #1f2a33" }}>
        <div style={{ fontSize: ".8rem", color: "#7dd3cf", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
          Step 1 · Your search phrases
        </div>
        <p style={{ color: "#a1a1aa", fontSize: ".92rem", margin: ".5rem 0 .9rem" }}>
          We'll build 2–3 real search phrases from <strong>this cycle's focus</strong> — written the way
          someone looking for you would actually type them into ChatGPT or Perplexity.
        </p>

        {!hasPhrases && (
          <button
            type="button"
            data-testid="baseline-generate-btn"
            onClick={doGenerate}
            disabled={generating}
            style={{ display: "inline-flex", alignItems: "center", gap: 8,
                     padding: "0.7rem 1.2rem", borderRadius: 999, border: "1px solid #2dd4bf",
                     background: "#0d2e2b", color: "#2dd4bf", fontWeight: 600, fontSize: ".92rem",
                     cursor: generating ? "not-allowed" : "pointer" }}
          >
            <Search size={16} /> {generating ? "Building your phrases…" : "Generate my search phrases"}
          </button>
        )}

        {generating && queueMsg && (
          <div data-testid="baseline-phrase-queue" style={{ marginTop: 10, color: "#7dd3cf", fontSize: ".88rem" }}>
            {queueMsg}
          </div>
        )}
        {phraseError && (
          <div data-testid="baseline-phrase-error" style={{ marginTop: 10, color: "#f4a3b0", fontSize: ".88rem" }}>
            {phraseError}
          </div>
        )}

        {hasPhrases && (
          <>
            <div data-testid="baseline-phrase-instructions"
                 style={{ marginBottom: 12, color: "#c7c7cf", fontSize: ".9rem", lineHeight: 1.5 }}>
              Paste each phrase into <strong>ChatGPT</strong> or <strong>Perplexity</strong> in an{" "}
              <strong>incognito or private window, logged out of your account</strong>. This shows what a
              stranger would actually see — not what AI remembers about you. See if you show up, and note
              what you find below.
            </div>
            <div data-testid="baseline-phrases" style={{ display: "grid", gap: 8 }}>
              {phrases.map((p, i) => (
                <div key={`phrase-${i}`} data-testid={`baseline-phrase-${i}`}
                     style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                              gap: 10, padding: "0.6rem 0.85rem", borderRadius: 10,
                              background: "#16181d", border: "1px solid #2a2e37" }}>
                  <span style={{ fontSize: ".95rem", color: "#e4e4e7" }}>“{p}”</span>
                  <button type="button" data-testid={`baseline-copy-${i}`} onClick={() => copyPhrase(p, i)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                   padding: "0.35rem 0.7rem", borderRadius: 8, cursor: "pointer",
                                   border: "1px solid #2a2e37", background: "#0f1115", color: "#a1a1aa", fontSize: ".82rem" }}>
                    {copiedIdx === i ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="baseline-regenerate-btn"
              onClick={doGenerate}
              disabled={generating}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10,
                       background: "none", border: "none", color: "#7dd3cf", fontSize: ".85rem",
                       cursor: generating ? "not-allowed" : "pointer" }}
            >
              <RotateCcw size={14} /> {generating ? "Regenerating…" : "Regenerate from this cycle's focus"}
            </button>
          </>
        )}
      </div>

      {/* Step 2 — Day 0 findings */}
      <div style={{ marginTop: "1.6rem" }}>
        <label htmlFor="baselineNote" style={{ fontSize: ".8rem", color: "#c7c7cf", fontWeight: 600 }}>
          Step 2 · Baseline — what I found
        </label>
        <textarea
          id="baselineNote"
          data-testid="baseline-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g., I didn't show up at all yet — mostly big publishers and generic advice."
          style={textareaStyle}
        />
      </div>

      <div data-testid="baseline-questions" role="group" aria-label="Baseline self-check questions"
           style={{ marginTop: "1.3rem", display: "grid", gap: "1rem" }}>
        {BASELINE_QUESTIONS.map((q) => (
          <div key={q.key} data-testid={`baseline-${q.key}`}>
            <div style={{ fontSize: ".92rem", color: "#e4e4e7", marginBottom: 8 }}>{q.text}</div>
            <div style={{ display: "flex", gap: 10 }}>
              {["yes", "no"].map((val) => {
                const selected = answers[q.key] === val;
                return (
                  <button
                    key={val}
                    type="button"
                    data-testid={`baseline-${q.key}-${val}`}
                    aria-pressed={selected}
                    onClick={() => setAnswer(q.key, val)}
                    style={{
                      padding: "0.5rem 1.3rem", borderRadius: 999, cursor: "pointer",
                      fontWeight: 600, fontSize: ".9rem",
                      border: selected ? "1px solid #2dd4bf" : "1px solid #2a2e37",
                      background: selected ? "#0d2e2b" : "#16181d",
                      color: selected ? "#2dd4bf" : "#a1a1aa",
                    }}
                  >
                    {val === "yes" ? "Yes" : "No"}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!complete && (
        <div data-testid="baseline-validation"
             style={{ marginTop: "1.1rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1a1410", border: "1px solid #4a2f17", color: "#f5c078", fontSize: ".9rem" }}>
          Add your baseline findings and answer all four questions before you can confirm.
        </div>
      )}

      <div style={{ marginTop: "1.4rem", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="baseline-save-draft-btn"
          onClick={onSaveDraft}
          disabled={busy}
          style={{ padding: "0.8rem 1.3rem", borderRadius: 999, cursor: busy ? "not-allowed" : "pointer",
                   border: "1px solid #2a2e37", background: "#16181d", color: "#e4e4e7", fontWeight: 600, fontSize: ".95rem" }}
        >
          Save draft
        </button>
        <button
          type="button"
          data-testid="baseline-confirm-btn"
          onClick={onConfirm}
          disabled={!canConfirm}
          style={{ display: "inline-flex", alignItems: "center", gap: 10,
                   padding: "0.85rem 1.4rem", borderRadius: 999, border: "none",
                   cursor: canConfirm ? "pointer" : "not-allowed",
                   background: canConfirm ? "#2dd4bf" : "#264c48", color: canConfirm ? "#04231f" : "#7dd3cf",
                   fontWeight: 700, fontSize: "1rem" }}
        >
          {busy ? "Working…" : "Confirm baseline & begin"} <ArrowRight size={18} />
        </button>
        <span data-testid="baseline-save-status" style={{ color: "#71717a", fontSize: ".82rem" }}>
          {savedFlash ? "✓ Draft saved"
            : complete && savedKey !== currentKey ? "Saving your draft…"
            : "Your draft saves automatically."}
        </span>
      </div>

      {error && (
        <div data-testid="baseline-error"
             style={{ marginTop: "1.2rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1c1417", border: "1px solid #4a1f27", color: "#f4a3b0", fontSize: ".9rem" }}>
          {error}
        </div>
      )}
    </div>
  );
}
